import React, { useEffect, useState } from "react";
import { Trophy, Coins, DollarSign, Crown, Medal } from "lucide-react";
import api from "../lib/api";

const WINDOWS = [
  { key: "week", label: "This week" },
  { key: "month", label: "This month" },
  { key: "all", label: "All time" },
];

/**
 * ReferralLeaderboardPanel — public ranking of top referrers.
 *
 * Pulls /api/referrals/leaderboard with a window toggle. Sanitised data —
 * no emails / IPs / user IDs of the *referred* users, only the leaders'
 * display names + aggregate metrics.
 */
export default function ReferralLeaderboardPanel() {
  const [win, setWin] = useState("week");
  const [rows, setRows] = useState(null);

  useEffect(() => {
    setRows(null);
    let mounted = true;
    api.get(`/referrals/leaderboard?window=${win}&limit=10`)
      .then((r) => { if (mounted) setRows(r.data?.leaders || []); })
      .catch(() => { if (mounted) setRows([]); });
    return () => { mounted = false; };
  }, [win]);

  return (
    <div data-testid="referral-leaderboard" className="bg-gradient-to-br from-fuchsia-500/10 to-tv-accent/10 border border-white/10 rounded-xl p-5">
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <Trophy className="w-5 h-5 text-tv-accent" />
        <h3 className="font-display font-black text-xl flex-1">Top inviters</h3>
        <div className="flex gap-1">
          {WINDOWS.map((w) => (
            <button
              key={w.key}
              data-testid={`referral-leaderboard-window-${w.key}`}
              onClick={() => setWin(w.key)}
              className={`text-[11px] font-bold px-2.5 py-1 rounded ${win === w.key ? "bg-tv-accent text-black" : "bg-white/5 text-white/70 hover:bg-white/10"}`}
            >{w.label}</button>
          ))}
        </div>
      </div>

      {rows == null ? (
        <div className="text-white/60 py-6 text-center text-sm">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-white/50 py-6 text-center text-sm">No approved invites in this window yet — be the first!</div>
      ) : (
        <ol className="space-y-1.5">
          {rows.map((r, i) => (
            <li
              key={r.user_id}
              data-testid={`referral-leader-${r.user_id}`}
              className={`flex items-center gap-3 rounded-lg px-3 py-2 ${
                i === 0 ? "bg-tv-accent/20 border border-tv-accent/40"
                  : i < 3 ? "bg-white/10 border border-white/10"
                  : "bg-white/5"
              }`}
            >
              <div className="shrink-0 w-7 h-7 rounded-full bg-black/60 flex items-center justify-center">
                {i === 0 ? <Crown className="w-4 h-4 text-tv-accent" />
                  : i === 1 ? <Medal className="w-4 h-4 text-white/80" />
                  : i === 2 ? <Medal className="w-4 h-4 text-amber-500" />
                  : <span className="text-[11px] font-black text-white/60">{i + 1}</span>}
              </div>
              {r.picture ? (
                <img src={r.picture} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-tv-accent/20 border border-tv-accent/40 shrink-0" />
              )}
              <div className="min-w-0 flex-1">
                <div className="font-bold truncate">{r.display_name}</div>
                <div className="text-white/50 text-xs">{r.referral_count} invite{r.referral_count === 1 ? "" : "s"} approved</div>
              </div>
              <div className="shrink-0 text-right">
                {r.cash_earned_usd > 0 ? (
                  <div className="inline-flex items-center gap-1 text-emerald-300 font-black text-sm">
                    <DollarSign className="w-3.5 h-3.5" /> {Number(r.cash_earned_usd).toFixed(0)}
                  </div>
                ) : (
                  <div className="inline-flex items-center gap-1 text-tv-accent font-black text-sm">
                    <Coins className="w-3.5 h-3.5" /> {(r.points_earned || 0).toLocaleString()}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
